import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Higher-order function that creates a conditional renderer for clickable items
 * @param {boolean} isTableListAction - Whether the action is table_list
 * @param {function} onClickHandler - Function to handle click events
 * @param {string} linkPath - Path for Link component (optional)
 * @param {object} linkParams - Parameters for Link component (optional)
 * @param {object} styles - Custom styles for the rendered element
 * @param {boolean} bypassLink - Whether to bypass Link wrapper when isTableListAction is false
 * @returns {function} A function that renders either a clickable div or a Link
 */
export const createConditionalRenderer = (
	isTableListAction,
	onClickHandler,
	linkPath = '',
	linkParams = {},
	styles = {},
	bypassLink = false
) => {
	return (content, itemId, customStyles = {}) => {
		const defaultClickableStyles = {
			whiteSpace: 'pre-wrap',
			wordWrap: 'break-word',
			cursor: 'pointer',
			textDecoration: 'underline',
			...styles,
			...customStyles,
		};

		const defaultLinkStyles = {
			whiteSpace: 'pre-wrap',
			wordWrap: 'break-word',
			...styles,
			...customStyles,
		};
		
		if (isTableListAction) {
			return (
				<div
					style={defaultClickableStyles}
					onClick={() => onClickHandler(itemId)}
				>
					{content}
				</div>
			);
		} else {
			// If bypassLink is true, just render the div without Link wrapper
			if (bypassLink) {
				return (
					<div style={defaultLinkStyles}>
						{content}
					</div>
				);
			}
			
			const finalLinkPath = typeof linkPath === 'function' 
				? linkPath(itemId) 
				: `${linkPath}/${itemId}`;
			
			return (
				<Link to={finalLinkPath} {...linkParams}>
					<div style={defaultLinkStyles}>
						{content}
					</div>
				</Link>
			);
		}
	};
};

/**
 * Higher-order component that wraps content with conditional rendering
 * @param {object} props - Component props
 * @param {boolean} props.isTableListAction - Whether the action is table_list
 * @param {function} props.onClickHandler - Function to handle click events
 * @param {string} props.linkPath - Path for Link component
 * @param {object} props.linkParams - Parameters for Link component
 * @param {object} props.styles - Custom styles
 * @param {React.ReactNode} props.children - Content to render
 * @param {string} props.itemId - ID of the item
 * @param {object} props.customStyles - Additional styles to merge
 * @param {boolean} props.bypassLink - Whether to bypass Link wrapper when isTableListAction is false
 * @returns {JSX.Element} Rendered component
 */
export const ConditionalRenderer = ({
	isTableListAction,
	onClickHandler,
	linkPath = '',
	linkParams = {},
	styles = {},
	children,
	itemId,
	customStyles = {},
	bypassLink = false,
}) => {
	const renderer = createConditionalRenderer(
		isTableListAction,
		onClickHandler,
		linkPath,
		linkParams,
		styles,
		bypassLink
	);

	return renderer(children, itemId, customStyles);
};

/**
 * Hook that returns a conditional renderer function
 * @param {boolean} isTableListAction - Whether the action is table_list
 * @param {function} onClickHandler - Function to handle click events
 * @param {string} linkPath - Path for Link component
 * @param {object} linkParams - Parameters for Link component
 * @param {object} styles - Custom styles
 * @param {boolean} bypassLink - Whether to bypass Link wrapper when isTableListAction is false
 * @returns {function} A function that renders conditional content
 */
export const useConditionalRenderer = (
	isTableListAction,
	onClickHandler,
	linkPath = '',
	linkParams = {},
	styles = {},
	bypassLink = false
) => {
	return React.useCallback(
		(content, itemId, customStyles = {}) => {
			const renderer = createConditionalRenderer(
				isTableListAction,
				onClickHandler,
				linkPath,
				linkParams,
				styles,
				bypassLink
			);
			return renderer(content, itemId, customStyles);
		},
		[isTableListAction, onClickHandler, linkPath, linkParams, styles, bypassLink]
	);
}; 